<template>
  <b-table
    :items="items.cards"
    :fields="fields"
    no-border-collapse
    responsive
    class="compact-table-sm card-counter-table"
    :class="compact ? 'card-counter-table-compact' : 'card-counter-table-full'"
    :sortBy.sync="localSortBy"
    :sortDesc.sync="localSortDesc"
    :tbody-transition-props="transProps"
    primary-key="title"
    :filter="filter"
  >
    <template v-slot:cell(quantity)="quantity">
      <div v-if="!compact">{{ quantity.value }}&times;</div>
      <div class="card-counter-buttons" :class="compact ? 'text-nowrap' : ''">
        <span v-if="compact" class="mr-2">{{ quantity.value }}&times;</span>

        <b-button
          v-if="showDiscard"
          variant="outline-danger"
          outline
          @click="$emit('umcc:discard', quantity.item.slug)"
          class="mb-1 mr-1"
          size="sm"
        >
          <font-awesome-icon :icon="['far', 'trash']" />

          <span v-if="!compact">To discard</span>
        </b-button>
        <b-button
          v-if="showDraw"
          variant="outline-primary"
          @click="$emit('umcc:draw', quantity.item.slug)"
          class="mb-1 mr-1"
          size="sm"
        >
          <font-awesome-icon :icon="['far', 'layer-group']" />

          <span v-if="!compact">To draw pile</span>
        </b-button>
        <b-button
          v-if="showHand"
          variant="outline-success"
          @click="$emit('umcc:hand', quantity.item.slug)"
          class="mb-1 mr-1"
          size="sm"
        >
          <font-awesome-icon :icon="['far', 'hand-paper']" />
          <span v-if="!compact">To hand</span>
        </b-button>
      </div>
    </template>

    <template v-slot:cell(title)="title">
      <div>{{ title.value }}</div>
      <b-img-lazy
        v-if="!compact && title.item.image"
        :src="title.item.image"
        width="100"
        height="140"
      />
    </template>

    <template v-slot:cell(type)="type">
      <img v-if="type.value === 'attack'" src="@/assets/images/attack.svg" />
      <img
        v-else-if="type.value === 'defence' || type.value === 'defense'"
        src="@/assets/images/defence.svg"
      />
      <img
        v-else-if="type.value === 'versatile'"
        src="@/assets/images/versatile.svg"
      />
      <img
        v-else-if="type.value === 'scheme'"
        src="@/assets/images/scheme.svg"
      />
    </template>

    <template v-slot:row-details="card">
      <b-row class="mx-sm-1 mx-md-5">
        <b-col>
          <p v-if="card.item.basicText">
            {{ card.item.basicText }}
          </p>
          <p v-if="card.item.type !== 'scheme' && card.item.immediateText">
            <strong>Immediately:</strong> {{ card.item.immediateText }}
          </p>
          <p v-if="card.item.type !== 'scheme' && card.item.duringText">
            <strong>During combat:</strong> {{ card.item.duringText }}
          </p>
          <p v-if="card.item.type !== 'scheme' && card.item.afterText">
            <strong>After combat:</strong> {{ card.item.afterText }}
          </p>
        </b-col>
      </b-row>
    </template>

    <template #head(hsProbability)>
      <div>In hand size {{ handSize }}</div>
    </template>
  </b-table>
</template>

<script>
export default {
  name: "CardCounterTable",
  props: {
    items: {
      type: Object,
      required: true,
    },
    showDraw: {
      type: Boolean,
      default: false,
    },
    showDiscard: {
      type: Boolean,
      default: false,
    },
    showHand: {
      type: Boolean,
      default: false,
    },
    showProbability: {
      type: Boolean,
      default: false,
    },
    handSize: {
      type: Number,
      default: 1,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    sortBy: {
      type: String,
    },
    sortDesc: {
      type: Boolean,
    },
    filter: {
      type: String,
    },
  },
  data() {
    return {
      transProps: {
        name: "card-counter-table",
      },
      fields: [
        { key: "quantity", label: "#", sortable: true, sortDirection: "desc" },
        { key: "title", sortable: true },
        { key: "characterName", sortable: true },
        {
          key: "type",
          sortable: true,
          tdClass: (value) => [
            value,
            "card-type",
            this.compact ? "text-center" : "align-middle text-right",
          ],
          thClass: "vertical",
        },
        {
          key: "value",
          tdClass: (value, key, item) => [
            item.type,
            "card-value",
            this.compact ? "text-right" : "align-middle",
          ],
          thClass: "vertical",
          sortable: true,
          sortDirection: "desc",
          sortByFormatted: true,
          formatter: (value, key, item) =>
            item.type === "scheme" ? null : value,
        },
        {
          key: "boost",
          tdClass: ["text-right"],
          thClass: "vertical",
          sortable: true,
          sortDirection: "desc",
          formatter: (value) => `B${value}`,
        },
        this.showProbability
          ? {
              key: "probability",
              label: "Draw probability",
              tdClass: ["text-right"],
              thClass: "vertical",
              sortable: true,
              sortDirection: "desc",
              sortByFormatted: this.sortFormat,
              formatter: this.formatProbability,
            }
          : null,
        this.showProbability && this.handSize > 0
          ? {
              key: "hsProbability",
              label: `In hand size ${this.handSize}`,
              tdClass: ["text-right"],
              thClass: "vertical",
              sortable: false,
              formatter: this.formatHandSizeProbability,
            }
          : null,
      ],
    };
  },
  computed: {
    localSortBy: {
      get() {
        if (!this.showProbability && this.sortBy === "probability")
          return "quantity";
        return this.sortBy;
      },
      set(value) {
        this.$emit("update:sortBy", value);
      },
    },
    localSortDesc: {
      get() {
        return this.sortDesc;
      },
      set(value) {
        this.$emit("update:sortDesc", value);
      },
    },
  },
  methods: {
    formatProbability(_, __, value) {
      const percent = (value.quantity / this.items.quantity).toLocaleString(
        "en",
        {
          style: "percent",
          maximumFractionDigits: 1,
        }
      );

      return `${percent} (${value.quantity}/${this.items.quantity})`;
    },
    sortFormat(_, __, value) {
      return value.quantity / this.items.quantity;
    },
    formatHandSizeProbability(_, __, value) {
      var prob = 1;
      if (this.handSize < 1 || value.quantity === 0) return 0;
      if (this.handSize > this.items.quantity - value.quantity) return 1;
      for (var i = 0; i < this.handSize; ++i) {
        prob *= 1 - value.quantity / (this.items.quantity - i);
      }
      return (1 - prob).toLocaleString("en", {
        style: "percent",
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style lang="less">
@import "~@/assets/less/mixins";
.card-counter-table-move {
  transition: all 0.75s;
}

// .card-counter-table-enter-active,
// .card-counter-table-leave-active {
//   transition: opacity 0.5s;
// }

.card-counter-table-enter,
.card-counter-table-leave-to {
  opacity: 0;
}

.card-counter-table-leave-active {
  position: absolute;
}
.card-counter-table {
  // Set minimum height to be all of the screen, minus footer and tab headers
  // Footer changes sizes at different breakpoints
  min-height: calc(100vh - 448px - 42px);
  @media (min-width: 576px) {
    min-height: calc(100vh - 200px - 42px);
  }
  @media (min-width: 768px) {
    min-height: calc(100vh - 176px - 42px);
  }
  @media (min-width: 992px) {
    min-height: calc(100vh - 176px - 42px);
  }
  @media (min-width: 1200px) {
    min-height: calc(100vh - 152px - 42px);
  }
  &.card-counter-table-full {
    .card-counter-buttons {
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;

      position: relative;
      display: inline-flex;
      vertical-align: middle;

      .btn {
        width: 100%;
        position: relative;
        flex: 1 1 auto;
      }
    }
    .card-type {
      border-radius: 0.5rem 0 0 0.5rem;
      padding-right: 0;
    }
    .card-value {
      font-size: 1.5rem;
      font-weight: bold;
      border-radius: 0 0.5rem 0.5rem 0;
    }
  }
  .attack {
    background-color: @attack-red !important;
    color: white;
    img {
      width: 2.1em;
    }
  }

  .defense,
  .defence {
    background-color: @defence-blue !important;
    color: white;
    img {
      width: 1.65em;
    }
  }

  .versatile {
    background-color: @versatile-violet !important;
    color: white;
    img {
      width: 2em;
    }
  }

  .scheme {
    background-color: @scheme-yellow !important;
    color: white;
    img {
      height: 2em;
      margin-right: -0.5em;
    }
  }

  // &.card-counter-table-compact {
  //   .attack {
  //     img {
  //       width: 1em;
  //     }
  //   }

  //   .defense,
  //   .defence {
  //     img {
  //       width: 0.8em;
  //     }
  //   }

  //   .versatile {
  //     img {
  //       width: 1em;
  //     }
  //   }

  //   .scheme {
  //     img {
  //       height: 1em;
  //     }
  //   }
  // }
}
</style>
